/**
 * Procedural motion (pure): what the captain does when the model has no clip for it (the stand-in model has only
 * `idle`), plus the entrance pop that every model gets. Each returns offsets the animator adds on top of the clips:
 * `hop`/`rise` in model units (the character is 1 tall), angles in radians, `squash` a scale on Y (X/Z keep volume).
 */
import type { BramClip } from "./bramContract";

export interface Offsets {
  readonly hop: number; readonly lean: number; readonly squash: number;
  readonly headPitch: number; readonly headYaw: number; readonly headRoll: number;
  /** Right/left upper arm raised (0 rest … 1 straight up), for models that have the arm but not the clip. */
  readonly armR: number; readonly armL: number;
}
export const REST: Offsets = { hop: 0, lean: 0, squash: 1, headPitch: 0, headYaw: 0, headRoll: 0, armR: 0, armL: 0 };

const DEG = Math.PI / 180;
const bump = (u: number) => Math.sin(Math.PI * Math.min(1, Math.max(0, u)));
/** Ease in-out 0→1→0 over u in [0, 1] with a hold in the middle. */
const plateau = (u: number, edge = 0.25) => (u <= 0 || u >= 1 ? 0 : u < edge ? bump(u / edge / 2) : u > 1 - edge ? bump((1 - u) / edge / 2) : 1);

/** Bounces in a stand-in's laugh: the art's 5.5 Hz over its 1.6 s clip. */
const LAUGH_BOUNCES = 9;

/** A stand-in for a missing clip, `u` its progress 0…1. */
export function gesture(clip: BramClip, u: number): Offsets {
  switch (clip) {
    case "nod": return { ...REST, headPitch: -12 * DEG * Math.abs(Math.sin(2 * Math.PI * u)) };
    case "cheer": return { ...REST, hop: 0.06 * bump(u * 2), squash: 1 + 0.06 * Math.sin(2 * Math.PI * u), headPitch: 8 * DEG * bump(u), armR: plateau(u) };
    case "wave": return { ...REST, headRoll: 6 * DEG * Math.sin(4 * Math.PI * u) * bump(u), headPitch: 4 * DEG * bump(u), armR: plateau(u, 0.2) };
    // no arm: a stand-in's arm cannot aim, and the look-at already turns him toward the target
    case "point": return { ...REST, lean: 5 * DEG * Math.min(1, u * 2), headPitch: -5 * DEG * Math.min(1, u * 2) };
    case "alarm": return { ...REST, lean: -7 * DEG * bump(u * 1.6), headPitch: 6 * DEG * bump(u * 1.6), hop: 0.02 * bump(u * 3) };
    case "think": return { ...REST, headRoll: 7 * DEG * plateau(u), headPitch: 5 * DEG * plateau(u), headYaw: 8 * DEG * plateau(u) };
    case "salute": return { ...REST, headPitch: 4 * DEG * plateau(u), squash: 1 + 0.02 * plateau(u), armR: 0.8 * plateau(u) };
    // a belly laugh: leans back and bounces (the art's 5.5 Hz), no arm
    case "laugh": return { ...REST, lean: -6 * DEG * plateau(u), hop: 0.012 * Math.abs(Math.sin(Math.PI * LAUGH_BOUNCES * u)) * plateau(u), headPitch: 6 * DEG * plateau(u) };
    case "thumbs_up": return { ...REST, headPitch: -8 * DEG * bump(u * 2), armR: 0.6 * plateau(u) };
    default: return REST;
  }
}

/** The entrance: he pops up from below the frame, overshoots with a squash, settles (`u` 0…1 over ENTER_MS). */
export const ENTER_MS = 520;
export function entrance(u: number): { rise: number; squash: number } {
  if (u >= 1) return { rise: 0, squash: 1 };
  const t = Math.max(0, u);
  // back-out rise: from 0.35 below to a small overshoot, then home
  const c = 1.9;
  const e = 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
  const squash = 1 + 0.12 * Math.sin(Math.PI * Math.min(1, t * 1.4)) * (t < 0.35 ? -1 : 1) * (1 - t);
  return { rise: -0.35 * (1 - e), squash };
}

/** A quick hop for a line that follows another without leaving (`u` 0…1 over HOP_MS). */
export const HOP_MS = 300;
export function hop(u: number): { rise: number; squash: number } {
  if (u >= 1 || u < 0) return { rise: 0, squash: 1 };
  return { rise: 0.03 * bump(u), squash: 1 + 0.04 * Math.sin(2 * Math.PI * u) };
}

/** A stand-in's talking head (no mouth to move): a small nod on every syllable arc. */
export function talkBob(mouthOpen: number): number { return -3 * DEG * mouthOpen; }
